<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="registration-verify-email">
    <div class="registration-verify-email__header">
      <h1 class="registration-verify-email__title ds1-utd-h2">
        {{ isUpdateEmailMode ? 'Update Email' : 'Register for a new account' }}
      </h1>
      <a class="registration-verify-email__privacy-link"
         href="/home/privacy-policy"
         target="_blank"
         rel="noopener noreferrer">Privacy policy</a>
    </div>
    <div class="registration-verify-email__container">
      <UtdOptionsCard class="primary-card">
        <template #content>
          <div class="registration-verify-email__inner-container">
            <h3 class="registration-verify-email__subtitle">Verify your email</h3>
            <div class="registration-verify-email__description">
              <p>
                Get off-network and mobile app access.
                We just sent an email to {{ displayEmail }}.
              </p>
              <p>The code is valid for 10 minutes.</p>
            </div>
            <div class="registration-verify-email__digit-entry">
              <UtdVerificationCodeInput ref="codeInputRef"
                                        v-model="form.verificationCode"
                                        @submit="handleFormSubmit" />
              <div v-if="isAuthErrorNotificationVisible"
                   class="registration-verify-email__inline-error">
                <UtdNotification id="twoFactorErrorNotification"
                                 :has-close-notification="false"
                                 notification-mode="error">
                  <template #title>
                    <h4>Error</h4>
                  </template>
                  <template #listContent>
                    <ul class="registration-verify-email__notification-list">
                      <li v-for="(message, index) in authErrorMessages"
                          :key="index"
                          class="registration-verify-email__notification-list-item">
                        <span v-html="message" />
                      </li>
                    </ul>
                  </template>
                </UtdNotification>
              </div>
              <p class="registration-verify-email__help-text">
                Enter the 6 digit code that appears in your email
              </p>
            </div>
            <div v-if="hasResentVerificationCode"
                 class="registration-verify-email__resend-confirmation">
              <i class="wkce-icon-filled-check-circle" />
              <p>A new code has been sent</p>
            </div>
            <div class="registration-verify-email__actions">
              <UtdButton v-if="showBackButton"
                         id="backToRegistrationButton"
                         button-size="large"
                         button-style="text"
                         type="button"
                         :disabled="isSubmitting"
                         @click="handleBackToRegistration">
                Back
              </UtdButton>
              <UtdButton v-if="!isResendCooldownActive"
                         id="resendVerificationButton"
                         button-size="large"
                         button-style="text"
                         type="button"
                         :disabled="isResendDisabled"
                         @click="handleSendVerificationCode({ resendCode: true })">
                Re-send Email
              </UtdButton>
              <span v-else
                    class="registration-verify-email__resend-status"
                    role="status"
                    aria-label="Sending Email. Re-send Email will be available soon.">
                <span class="registration-verify-email__resend-status-text">Sending Email</span>
                <span class="registration-verify-email__resend-ring"
                      :style="{ '--cooldown-progress': `${resendCooldownProgress}%` }"
                      aria-hidden="true" />
              </span>
              <UtdButton id="submitVerificationButton"
                         :disabled="isSubmitCodeDisabled"
                         button-size="large"
                         type="button"
                         @click="handleFormSubmit">
                {{ hasPendingContactVerification ? 'Save Changes' : 'Verify' }}
              </UtdButton>
            </div>
          </div>
        </template>
      </UtdOptionsCard>
      <div v-if="showSkipOption"
           class="registration-verify-email__or-divider">
        <div class="registration-verify-email__or-line" />
        <span class="registration-verify-email__or-text">or</span>
        <div class="registration-verify-email__or-line" />
      </div>
      <UtdOptionsCard v-if="showSkipOption" class="primary-card">
        <template #content>
          <div class="registration-verify-email__inner-container">
            <h3 class="registration-verify-email__subtitle">Continue to UpToDate</h3>
            <p class="registration-verify-email__deferred-description">
              You can verify your email at a later time to get off-network and mobile
              app access. Simply resend the verification email from your 'My Account'
              settings while on-network.
            </p>
            <div class="registration-verify-email__actions">
              <UtdButton id="continueToUpToDateButton"
                         button-size="large"
                         button-style="ghost"
                         type="button"
                         @click="handleContinueToUpToDate">
                Continue to UpToDate
              </UtdButton>
            </div>
          </div>
        </template>
      </UtdOptionsCard>
    </div>
  </div>
</template>

<script setup>
import UtdVerificationCodeInput
    from '_acaSrc/components/shared/stdlib/UtdVerificationCodeInput.vue';
import UtdOptionsCard from '_acaSrc/components/shared/stdlib/UtdOptionsCard.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import useRegistrationEmailVerificationFlow
    from '_acaSrc/composables/registrationEmailVerificationFlow.composable';
import { useStore } from 'vuex';
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { getWindow, getDocument } from '_acaSrc/utility/DOM';
import {
    C_HTTP,
    C_REG_MERGE,
    EMAIL_VERIFIED_PARAM,
    REG_FIELD,
    REGISTRATION_DEFAULT_ERROR_MESSAGE,
    REGISTRATION_FIELD_ERROR,
    REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE,
    REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE,
    REGISTRATION_DEFERRED_EXIT_BROWSER_ERROR_MESSAGE,
    ACCOUNT_UPDATE_EXIT_BROWSER_ERROR_MESSAGE,
    ACCOUNT_UPDATE_DEFERRED_EXIT_BROWSER_ERROR_MESSAGE,
    VERIFY_EMAIL_RETURN_TO_PARAM,
    UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES
} from '_acaSrc/utility/constants';
import { getQueryParamValues, setQueryParamValue } from '_acaSrc/utility/http';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import PubSub from '_acaSrc/utility/PubSub';
import {
    EMAIL_VERIFICATION_EVENTS,
    trackRegistrationEmailVerifyUiEvent
} from '_acaSrc/utility/emailVerificationEvents';

import {
    SET_PENDING_CONTACT_VERIFICATION,
    SET_PENDING_EMAIL_VERIFICATION_FLOW
} from '_acaSrc/store/account.store';
import { SET_OTC_IS_ALLOW_OTC_GENERATION } from '_acaSrc/store/auth.store';

const store = useStore();

const pubsub = new PubSub();
const RESEND_COOLDOWN_SECONDS = 10;

const {
    form,
    isDirty,
    serverError,
    needsNewCode,
    isSubmitDisabled,
    isSendNewCodeDisabled,
    sendCodeAttempts,
    checkResetServerError,
    setServerError,
    resetForm,
    resetAllServerErrors,
    checkFieldInvalid,
    setupFormPayload,
    sanitizeForm,
    sendTwoFactorCodeRequest
} = useRegistrationForm();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const registrationInfo = computed(() => store.getters['login/registrationInfo']);
const router = computed(() => store.getters['app/router']);
const userLoggedIn = computed(() => store.getters['user/userLoggedIn']);
const profileEmail = computed(() => store.getters['profile/userEmail']);
const pendingContactVerification = computed(
    () => store.getters['account/pendingContactVerification']
);
const pendingEmailVerificationFlow = computed(
    () => store.getters['account/pendingEmailVerificationFlow']
);
const isEmailVerified = computed(() => store.getters['profile/isEmailVerified']);
const isTwoFactorEmailVerificationAllowDefer = computed(
    () => store.getters['feature/isTwoFactorEmailVerificationAllowDefer']
);

const hasResentVerificationCode = ref(false);
const isAuthErrorNotificationVisible = ref(false);
const isSubmitting = ref(false);
const isSendingVerificationCode = ref(false);
const resendCooldownIntervalId = ref(null);
const resendCooldownSeconds = ref(0);
const isAllowOtcGeneration = ref(store.getters['auth/otcIsAllowOtcGeneration'] ?? true);
const isAllowOtcEntry = ref(true);
const isProfileOnNetwork = computed(() => store.getters['profile/isOnNetwork']);

const getOtcStateData = (source) => {
    const data = source?.data ?? source ?? {};
    return data?.emailVerificationResponse ?? data;
};
const {
    hasPendingContactVerification,
    isDeferredUser,
    isAccountUpdateFlow,
    verificationFlow,
    hasPreSentVerificationCode,
    trackingFlow,
    showSkipOption,
    isUpdateEmailMode,
    showBackButton,
    displayEmail
} = useRegistrationEmailVerificationFlow({
    registrationInfo,
    userLoggedIn,
    pendingContactVerification,
    pendingEmailVerificationFlow,
    isTwoFactorEmailVerificationAllowDefer,
    isProfileOnNetwork,
    profileEmail,
    form
});

const isVerificationOnlyFlow = computed(() => verificationFlow.value !== 'registration');
const isOffNetworkVerificationBlocked = computed(() =>
    isVerificationOnlyFlow.value
    && userLoggedIn.value
    && isProfileOnNetwork.value === false
    && isEmailVerified.value === false
);

const codeInputRef = ref(null);

const navigateBackToPreviousPage = () => {
    const returnTo = getQueryParamValues(
        getWindow().location.href,
        VERIFY_EMAIL_RETURN_TO_PARAM
    );
    if (returnTo && returnTo.startsWith('/')) {
        router.value.url(returnTo);
        return;
    }
    if (getWindow().history.length > 2) {
        getWindow().history.go(-2);
        return;
    }
    if (getWindow().history.length > 1) {
        getWindow().history.back();
        return;
    }
    router.value.url('/login-register');
};

watch(() => form.verificationCode, (newVal) => {
    isDirty.verificationCode = newVal.length > 0;
    checkResetServerError(REG_FIELD.VERIFICATION_CODE);
});

const isVerificationBlocked = computed(() =>
    checkFieldInvalid(REG_FIELD.VERIFICATION_CODE)
    || !isAllowOtcEntry.value
    || needsNewCode.value
    || isSubmitting.value
);

const isSubmitCodeDisabled = computed(() =>
    isOffNetworkVerificationBlocked.value
        ? true
        : isVerificationOnlyFlow.value
            ? isVerificationBlocked.value
            : isSubmitDisabled.value || isVerificationBlocked.value
);

const isResendCooldownActive = computed(() =>
    isSendingVerificationCode.value || resendCooldownSeconds.value > 0
);

const resendCooldownProgress = computed(() => {
    if (!resendCooldownSeconds.value) {
        return 0;
    }
    return (resendCooldownSeconds.value / RESEND_COOLDOWN_SECONDS) * 100;
});

const isResendDisabled = computed(() =>
    isOffNetworkVerificationBlocked.value
    || isSendNewCodeDisabled.value
    || !isAllowOtcGeneration.value
    || isSubmitting.value
    || resendCooldownSeconds.value > 0
    || (!isVerificationOnlyFlow.value && isSubmitDisabled.value)
);

const authErrorMessages = computed(() => {
    if (serverError.twoFactor) {
        return [ ...serverError.twoFactor ];
    }
    return [ ...UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES ];
});

const handleOtcState = (error) => {
    const otcData = getOtcStateData(error);
    if (typeof otcData.isAllowOtcGeneration !== 'boolean') {
        return;
    }
    isAllowOtcGeneration.value = otcData.isAllowOtcGeneration;
    store.commit(`auth/${SET_OTC_IS_ALLOW_OTC_GENERATION}`, otcData.isAllowOtcGeneration);
    if (typeof otcData.isAllowOtcEntry !== 'boolean') {
        return;
    }
    isAllowOtcEntry.value = otcData.isAllowOtcEntry;
    setOtcErrorMessages();
};

const getExitBrowserErrorMessage = () => {
    const isAccountUpdateOrContactChange = trackingFlow.value === 'account-update' 
        || trackingFlow.value === 'contact-information-change';
    
    if (isAccountUpdateOrContactChange) {
        return showSkipOption.value 
            ? ACCOUNT_UPDATE_DEFERRED_EXIT_BROWSER_ERROR_MESSAGE
            : ACCOUNT_UPDATE_EXIT_BROWSER_ERROR_MESSAGE;
    }
    return showSkipOption.value
        ? REGISTRATION_DEFERRED_EXIT_BROWSER_ERROR_MESSAGE
        : REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE;
};

const setOtcErrorMessages = () => {
    if (!isAllowOtcEntry.value && !isAllowOtcGeneration.value) {
        setServerError('twoFactor', getExitBrowserErrorMessage());
        isAuthErrorNotificationVisible.value = true;
    }
    else if (!isAllowOtcEntry.value) {
        setServerError('twoFactor', REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE);
        isAuthErrorNotificationVisible.value = true;
    }
    else {
        checkResetServerError('twoFactor');
    }
};

const handleGeneralServerError = (error) => {
    if (!error) {
        return;
    }
    handleOtcState(error);
    const otcData = getOtcStateData(error);
    if (error.error?.rawResponse) {
        handleRawErrorResponse(error.error.rawResponse);
    }
    else if (Object.keys(otcData).length > 0) {
        handleErrorData(otcData);
    }
    else {
        setServerError('twoFactor', REGISTRATION_DEFAULT_ERROR_MESSAGE);
    }
    isAuthErrorNotificationVisible.value = true;
};

const handleErrorData = (otcData) => {
    if (!otcData || Object.keys(otcData).length === 0) {
        return;
    }
    if (otcData.errorMessage) {
        setServerError(REG_FIELD.VERIFICATION_CODE, otcData.errorMessage);
    }
};

const handleRawErrorResponse = (error) => {
    if (error.includes(REG_FIELD.EMAIL)) {
        setServerError(REG_FIELD.EMAIL, REGISTRATION_FIELD_ERROR.INCORRECT_EMAIL);
        needsNewCode.value = true;
    }
    else if (error.includes('code')) {
        setServerError(REG_FIELD.VERIFICATION_CODE, REGISTRATION_FIELD_ERROR.INVALID_CODE);
    }
};

const getFormFields = (response) => {
    if (Array.isArray(response?.formFields)) {
        return response.formFields;
    }
    if (Array.isArray(response?.data?.formFields)) {
        return response.data.formFields;
    }
    return [];
};

const getBooleanValue = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return null;
};

const getDisableSendingEmail = (response) => {
    const disableSendingEmailField = getFormFields(response)
        .find(({ name }) => name === 'disableSendingEmail');
    if (!disableSendingEmailField) {
        return false;
    }
    const parsedValue = getBooleanValue(disableSendingEmailField.value);
    if (typeof parsedValue === 'boolean') {
        return parsedValue;
    }
    return true;
};

const sendCodeStrategies = {
    pendingSaveVerification: {
        request: async() => {
            const { contactInfo, userProfession } = pendingContactVerification.value;
            return await utdRest('myAccount/sendVerificationCode', {
                contactInfo,
                userProfession
            });
        },
        isSuccess: response => !!response?.data
    },
    postSaveVerification: {
        request: async() => await utdRest('myAccount/sendEmailVerification'),
        isSuccess: response => !!response?.data
    },
    registration: {
        request: async() => await sendTwoFactorCodeRequest(),
        isSuccess: response => !!response?.isSuccess
    }
};

const applySendCodeSuccessState = (response) => {
    const shouldDisable = getDisableSendingEmail(response);
    isAllowOtcGeneration.value = !shouldDisable;
    store.commit(`auth/${SET_OTC_IS_ALLOW_OTC_GENERATION}`, !shouldDisable);
    isAuthErrorNotificationVisible.value = false;
    sendCodeAttempts.value += 1;
};

const getSendCodeStrategy = () => {
    return sendCodeStrategies[verificationFlow.value] || sendCodeStrategies.registration;
};

const executeSendCode = async() => {
    const strategy = getSendCodeStrategy();
    const response = await strategy.request();
    if (strategy.isSuccess(response)) {
        applySendCodeSuccessState(response);
        return true;
    }
    handleSendCodeFailure(response);
    return false;
};

const prepareResend = () => {
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.resendBtn, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
    hasResentVerificationCode.value = true;
    isAllowOtcEntry.value = true;
    checkResetServerError(REG_FIELD.VERIFICATION_CODE);
};

const clearPendingContactVerification = () => {
    store.commit(`account/${SET_PENDING_CONTACT_VERIFICATION}`, {});
};

const getHomeUrlWithEmailVerified = () => setQueryParamValue(
    C_HTTP.DEFAULT_HOMEPAGE_PATH,
    EMAIL_VERIFIED_PARAM,
    'true'
);

const resetVerificationEntryState = () => {
    clearResendCooldown();
    codeInputRef.value?.reset();
    form.verificationCode = '';
    isDirty.verificationCode = false;
    isAuthErrorNotificationVisible.value = false;
    resetAllServerErrors();
    needsNewCode.value = false;
    sendCodeAttempts.value = 0;
};

const handleSendVerificationCode = async({ resendCode = false }) => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    isSendingVerificationCode.value = true;
    if (resendCode) {
        prepareResend();
    }
    needsNewCode.value = false;
    resetAllServerErrors();
    isAuthErrorNotificationVisible.value = false;
    try {
        const didSend = await executeSendCode();
        if (didSend) {
            if (resendCode) {
                startResendCooldown();
            }
        }
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSendingVerificationCode.value = false;
        isSubmitting.value = false;
    }
};

const startResendCooldown = () => {
    clearResendCooldown();
    resendCooldownSeconds.value = RESEND_COOLDOWN_SECONDS;
    resendCooldownIntervalId.value = getWindow().setInterval(() => {
        if (resendCooldownSeconds.value <= 1) {
            clearResendCooldown();
            return;
        }
        resendCooldownSeconds.value -= 1;
    }, 1000);
};

const clearResendCooldown = () => {
    if (resendCooldownIntervalId.value) {
        getWindow().clearInterval(resendCooldownIntervalId.value);
    }
    resendCooldownIntervalId.value = null;
    resendCooldownSeconds.value = 0;
};

const handleSendCodeFailure = (response) => {
    const fields = getFormFields(response);
    fields.forEach((field) => {
        if (field.errorMessage) {
            setServerError(field.name, field.errorMessage);
        }
    });
    isAuthErrorNotificationVisible.value = true;
};

const submitStrategies = {
    pendingSaveVerification: async() => {
        const { contactInfo, userProfession } = pendingContactVerification.value;
        return await utdRest('profile/submitContact', {
            contactInfo,
            userProfession,
            code: form.verificationCode
        });
    },
    postSaveVerification: async() => {
        return await utdRest('myAccount/verifyEmail', {
            code: form.verificationCode
        });
    },
    registration: async() => {
        const sanitizedForm = sanitizeForm({ ...form });
        const responseForm = setupFormPayload(sanitizedForm);
        return await utdRest('register/submit', {
            form: {
                formName: registrationForm.value.formName,
                ...responseForm
            },
            twoFactorCode: form.verificationCode,
            termsAccepted: form.termsAccepted
        });
    }
};

const executeSubmit = async() => {
    const submitStrategy = submitStrategies[verificationFlow.value] || 
        submitStrategies.registration;
    return await submitStrategy();
};

const handleFormSubmit = async() => {
    if (isSubmitting.value || isOffNetworkVerificationBlocked.value) {
        return;
    }
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.verifyBtn, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
    isSubmitting.value = true;
    isAuthErrorNotificationVisible.value = false;
    resetAllServerErrors();
    try {
        const response = await executeSubmit();
        handleSubmitResponse(response);
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};

const clearAccountUpdateFlow = () => {
    store.commit(`account/${SET_PENDING_EMAIL_VERIFICATION_FLOW}`, false);
};

const handleSubmitSuccess = () => {
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.success, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
    resetForm();
    if (hasPendingContactVerification.value) {
        clearPendingContactVerification();
        getWindow().location.href = getHomeUrlWithEmailVerified();
        return;
    }
    if (isAccountUpdateFlow.value) {
        clearAccountUpdateFlow();
        pubsub.publish('showEmailVerifiedToast');
        getWindow().location.href = getHomeUrlWithEmailVerified();
        return;
    }
    const regSourceParam = getUserOriginParam();
    let url = setQueryParamValue(
        C_HTTP.DEFAULT_HOMEPAGE_PATH,
        C_REG_MERGE.REG_MERGE_SUCCESS_PARAM,
        regSourceParam
    );
    if (isDeferredUser.value) {
        url = setQueryParamValue(url, EMAIL_VERIFIED_PARAM, 'true');
    }
    store.dispatch('login/setOidcPartnerModalShown');
    getWindow().location.href = url;
};

const handleSubmitFailure = (response) => {
    handleOtcState(response);
    const otcData = getOtcStateData(response);
    if (otcData.errorMessage && !serverError[REG_FIELD.VERIFICATION_CODE]) {
        handleErrorData(otcData);
    }
    const fields = getFormFields(response);
    fields.forEach((field) => {
        if (field.errorMessage) {
            setServerError(field.name, field.errorMessage);
        }
    });
    isAuthErrorNotificationVisible.value = true;
};

const getSubmitResponsePayload = (response) => {
    return response?.data?.data ?? response?.data ?? response ?? {};
};

const getPlainObject = (value) => {
    return value && typeof value === 'object' ? value : {};
};

const isSubmitSuccessResponse = (response) => {
    const payload = getSubmitResponsePayload(response);
    const responseData = getPlainObject(response);
    const payloadData = getPlainObject(payload);
    const responseEnvelope = getPlainObject(responseData.data);
    const emailVerificationResponse = getPlainObject(
        payloadData.emailVerificationResponse || responseData.emailVerificationResponse
    );
    const contactInfoResponse = getPlainObject(
        payloadData.contactInfoResponse || responseData.contactInfoResponse
    );
    const successValues = [
        responseData.isSuccess,
        payloadData.isSuccess,
        responseData.data === true,
        payload === true,
        payloadData.isValid,
        responseEnvelope.isValid,
        contactInfoResponse.isSuccess,
        emailVerificationResponse.isValid
    ];
    return successValues.some(Boolean);
};

const handleSubmitResponse = (response) => {
    if (response?.isRedirect) {
        return;
    }
    if (isSubmitSuccessResponse(response)) {
        handleSubmitSuccess();
    }
    else {
        handleSubmitFailure(response);
    }
};

const getUserOriginParam = () => {
    if (store.getters['auth/isOidcUser']) {
        return C_REG_MERGE.MESSAGES.BYOL_REG.name;
    }
    return C_REG_MERGE.MESSAGES.REG.name;
};

const handleContinueToUpToDate = () => {
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.skipBtn, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
    resetForm();
    if (isAccountUpdateFlow.value) {
        clearAccountUpdateFlow();
        getWindow().location.href = C_HTTP.DEFAULT_HOMEPAGE_PATH;
        return;
    }
    const regSourceParam = getUserOriginParam();
    const url = setQueryParamValue(
        C_HTTP.DEFAULT_HOMEPAGE_PATH,
        C_REG_MERGE.REG_MERGE_SUCCESS_PARAM,
        regSourceParam
    );
    store.dispatch('login/setOidcPartnerModalShown');
    getWindow().location.href = url;
};

const handleBackToRegistration = () => {
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.backBtn, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
    resetVerificationEntryState();
    if (hasPendingContactVerification.value) {
        navigateBackToPreviousPage();
        pubsub.publish('wkutd.reopenContactInformationDialogFromEmailVerification');
        return;
    }
    router.value.url('/login-register');
};

const initializeVerification = async() => {
    if (verificationFlow.value === 'pendingSaveVerification') {
        if (hasPreSentVerificationCode.value) {
            return;
        }
        await handleSendVerificationCode({ resendCode: false });
        return;
    }
    if (verificationFlow.value === 'postSaveVerification') {
        if (!userLoggedIn.value) {
            navigateBackToPreviousPage();
            return;
        }
        await handleSendVerificationCode({ resendCode: false });
        return;
    }
    if (!form.email || !form.registrationUserName) {
        navigateBackToPreviousPage();
    }
};

const handlePopState = () => {
    if (isSubmitting.value) {
        return;
    }
    if (hasPendingContactVerification.value) {
        router.value.url(C_HTTP.DEFAULT_HOMEPAGE_PATH);
        clearPendingContactVerification();
    }
    else if (isAccountUpdateFlow.value) {
        clearAccountUpdateFlow();
        getWindow().location.href = C_HTTP.DEFAULT_HOMEPAGE_PATH;
    }
    else if (isDeferredUser.value) {
        const regSourceParam = getUserOriginParam();
        const url = setQueryParamValue(
            C_HTTP.DEFAULT_HOMEPAGE_PATH,
            C_REG_MERGE.REG_MERGE_SUCCESS_PARAM,
            regSourceParam
        );
        getWindow().location.href = url;
    }
    else {
        handleBackToRegistration();
    }
};

const handleLogoClick = () => {
    trackRegistrationEmailVerifyUiEvent(EMAIL_VERIFICATION_EVENTS.logoClick, {
        flow: trackingFlow.value,
        source: 'registrationEmailVerification'
    });
};

onMounted(async() => {
    const logo = getDocument().querySelector('.wk-logo');
    if (logo) {
        logo.addEventListener('click', handleLogoClick);
    }
    getWindow().history.pushState(null, '', getWindow().location.href);
    getWindow().addEventListener('popstate', handlePopState);
    if (hasPendingContactVerification.value || isAccountUpdateFlow.value) {
        await initializeVerification();
        return;
    }
    if (registrationForm.value.isLoaded) {
        await initializeVerification();
        return;
    }
    const unwatch = watch(
        () => registrationForm.value.isLoaded,
        async(loaded) => {
            if (loaded) {
                unwatch();
                await initializeVerification();
            }
        }
    );
});

onBeforeUnmount(() => {
    clearResendCooldown();
    const logo = getDocument().querySelector('.wk-logo');
    if (logo) {
        logo.removeEventListener('click', handleLogoClick);
    }
    getWindow().removeEventListener('popstate', handlePopState);
});
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.registration-verify-email {
  .ds1-ph-3();
  .ds1-pv-5();
  .ds1-flex-horizontal-center();
  flex-direction: column;
  align-items: center;
  max-width: 690px;
  margin: 0 auto;
  font-size: 14px;
}

@media only screen and (min-width: 768px) {
  .registration-verify-email__inner-container {
    .ds1-ph-2();
  }

  .registration-verify-email {
    :deep(.options-card .options-card__content) {
      .ds1-pb-4();
    }
  }
}

.registration-verify-email__container {
  width: 100%;
}

.registration-verify-email__header {
  .ds1-mb-2();
  width: 100%;
}

.registration-verify-email__title {
  .ds1-mb-1();
}

.registration-verify-email__privacy-link {
  color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
  font-size: 14px;

  &:hover {
    color: @WKCE-BLUE-SHADE1;
    text-decoration: underline;
  }
}

.registration-verify-email__subtitle {
  .ds1-mt-3();
  .ds1-mb-2();
  .ds1-utd-h4();
}

.registration-verify-email__description {
  .ds1-mb-2();
  font-size: 14px;
  line-height: 21px;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;

  p + p {
    .ds1-mt-1();
  }
}

.registration-verify-email__digit-entry {
  .ds1-mb-2();
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.registration-verify-email__inline-error {
  width: 100%;
  max-width: 336px;
}

.registration-verify-email__help-text {
  font-size: 14px;
  line-height: 21px;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
  text-align: center;
}

.registration-verify-email__resend-confirmation {
  .ds1-flex-vertical-center();
  .ds1-utd-weight-600();
  .ds1-mb-1();
  gap: 4px;
  font-size: 16px;
}

.wkce-icon-filled-check-circle {
  color: @DS1-UTD-SUCCESS;
  font-size: 20px;
}

.registration-verify-email__actions {
  .ds1-mt-1();
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;

    #backToRegistrationButton {
    margin-right: auto;
  }
}

.registration-verify-email__resend-status {
    .ds1-utd-size-2();
    display: inline-flex;
    align-items: center;
    gap: 0.25em;
    color: @WKCE-GRAY-SHADE1;
    white-space: nowrap;
}

.registration-verify-email__resend-ring {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background:
        conic-gradient(
            transparent calc(100% - var(--cooldown-progress)),
            @WKCE-BLACK 0
        );
    transition: background 1s linear;

    &::after {
        display: block;
        width: 12px;
        height: 12px;
        margin: 2px;
        border-radius: 50%;
        background: @DS1-UTD-GRAY-BG-COLOR;
        content: '';
    }
}

.registration-verify-email__notification-list {
  .ds1-mv-1();
  .ds1-pl-0();
  width: 90%;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}

.registration-verify-email__notification-list-item {
  margin-bottom: 4px;
}

.registration-verify-email__or-divider {
  .ds1-mv-3();
  display: flex;
  align-items: center;
  gap: 16px;
}

.registration-verify-email__or-line {
  flex: 1;
  height: 1px;
  background-color: @WKCE-GRAY-TINT4;
}

.registration-verify-email__or-text {
  .ds1-utd-weight-600();
  font-size: 28px;
  color: @WKCE-GRAY-TINT1;
}

.registration-verify-email__deferred-description {
  .ds1-mb-3();
  font-size: 14px;
  line-height: 21px;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}
</style>
